import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView } from '../utils/analytics';

interface RouteInfo {
  path: string;
  title: string;
  params?: Record<string, string>;
}

// Map of routes to user-friendly names
const routeMapping: Record<string, (params?: Record<string, string>) => RouteInfo> = {
  '/': () => ({ path: '/', title: 'Home' }),
  '/category/:categoryId': (params) => ({ 
    path: `/category/${params?.categoryId}`, 
    title: 'Category',
    params
  }),
  '/coloring/:categoryId/:imageId': (params) => ({ 
    path: `/coloring/${params?.categoryId}/${params?.imageId}`, 
    title: 'Coloring',
    params
  }),
  '/settings': () => ({ path: '/settings', title: 'Settings' }),
  '/privacy-policy': () => ({ path: '/privacy-policy', title: 'Privacy Policy' }),
};

// Extract parameters from pathname
const extractParams = (pathname: string): Record<string, string> => {
  const params: Record<string, string> = {};
  
  // Match category routes
  const categoryMatch = pathname.match(/\/category\/([^/]+)/);
  if (categoryMatch) {
    params.categoryId = categoryMatch[1];
  }
  
  // Match coloring routes
  const coloringMatch = pathname.match(/\/coloring\/([^/]+)\/([^/]+)/);
  if (coloringMatch) {
    params.categoryId = coloringMatch[1];
    params.imageId = coloringMatch[2];
  }
  
  return params;
};

// Get route info from pathname
const getRouteInfo = (pathname: string): RouteInfo => {
  const params = extractParams(pathname);
  
  // Try to match exact routes first
  if (routeMapping[pathname]) {
    return routeMapping[pathname](params);
  }
  
  // Try to match parameterized routes
  for (const [route, handler] of Object.entries(routeMapping)) {
    if (route.includes(':')) {
      const routePattern = route.replace(/:[^/]+/g, '[^/]+');
      const regex = new RegExp(`^${routePattern}$`);
      if (regex.test(pathname)) {
        return handler(params);
      }
    }
  }
  
  // Fallback for unknown routes
  return { path: pathname, title: 'Unknown Page' };
};

export const useRouteTracking = () => {
  const location = useLocation();
  
  useEffect(() => {
    const routeInfo = getRouteInfo(location.pathname);
    
    // Track the page view with comprehensive data
    trackPageView(routeInfo.title, {
      route_path: routeInfo.path,
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      referrer: document.referrer,
      timestamp: new Date().toISOString(),
      ...routeInfo.params
    });
    
  }, [location.pathname, location.search, location.hash]);
  
  return { currentRoute: getRouteInfo(location.pathname) };
}; 